import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'

// V1 Core Pages
import Login from './pages/Login'
import Register from './pages/Register'
import NewsHub from './pages/NewsHub'
import MarketMapView from './pages/MarketMapView'
import MapList from './pages/MapList'
import MapEditor from './pages/MapEditor'
import MapPublicView from './pages/MapPublicView'
import Profile from './pages/Profile'
import Settings from './pages/Settings'
import SharedMapView from './pages/SharedMapView'
import TelegramGuide from './pages/TelegramGuide'
import SavedNews from './pages/SavedNews'
import ProjectDocs from './pages/ProjectDocs'
import ProjectMindmap from './pages/ProjectMindmap'
import ProjectMindmapDetail from './pages/ProjectMindmapDetail'
import TrainingPage from './pages/TrainingPage'

// Admin Pages
import MarketMapManagement from './pages/admin/MarketMapManagement'
import MarketMapEditor from './pages/admin/MarketMapEditor'
import UserManagement from './pages/admin/UserManagement'
import TeamManagement from './pages/admin/TeamManagement'
import IframeDomainManagement from './pages/admin/IframeDomainManagement'
import PushNotificationAdmin from './pages/admin/PushNotificationAdmin'
import DriveSettings from './pages/admin/DriveSettings'
import ProjectMindmapAdmin from './pages/admin/ProjectMindmapAdmin'
import PermissionSettings from './pages/admin/PermissionSettings'

// Components
import ProtectedRoute from './components/common/ProtectedRoute'
import RoleRoute from './components/common/RoleRoute'
import MainLayout from './components/layout/MainLayout'
import PublicLayout from './components/layout/PublicLayout'

// Wrapper component để chọn layout phù hợp
function AppLayout() {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated ? <MainLayout /> : <PublicLayout />
}

function App() {
  const { isAuthenticated } = useAuthStore()

  return (
    <Routes>
      {/* Auth routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        }
      />
      <Route
        path="/register"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <Register />
        }
      />

      {/* Shared Map View (public - no layout) */}
      <Route path="/map/share/:sessionCode" element={<SharedMapView />} />

      {/* Property Map Public View (no layout) */}
      <Route path="/map/view/:shareToken" element={<MapPublicView />} />

      {/* Training Pages (standalone - no layout) */}
      <Route path="/training/:slug" element={<TrainingPage />} />

      {/* Map Editor - Full screen (outside MainLayout) */}
      <Route
        path="/map/create"
        element={
          <ProtectedRoute>
            <MapEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/map/edit/:mapId"
        element={
          <ProtectedRoute>
            <MapEditor />
          </ProtectedRoute>
        }
      />

      {/* Main App Routes với Layout động */}
      <Route path="/" element={<AppLayout />}>
        {/* Redirect / to /news */}
        <Route index element={<Navigate to="/news" replace />} />

        {/* PUBLIC PAGES - Accessible without login */}
        {/* News Hub - Tin tức BĐS */}
        <Route path="news" element={<NewsHub />} />
        <Route path="news/:id" element={<NewsHub />} />

        {/* Market Map - Bản đồ giá thị trường */}
        <Route path="map" element={<MarketMapView />} />
        <Route path="map/:mapId" element={<MarketMapView />} />

        {/* Drive Docs - Tài liệu dự án */}
        <Route path="docs" element={<ProjectDocs />} />

        {/* Project Mindmap - Thông tin dự án */}
        <Route path="projects" element={<ProjectMindmap />} />
        <Route path="projects/:slug" element={<ProjectMindmapDetail />} />

        {/* PROTECTED PAGES - Require login */}
        {/* Tạo bản đồ - Property Maps cá nhân */}
        <Route path="maps" element={<ProtectedRoute><MapList /></ProtectedRoute>} />

        {/* Profile & Settings */}
        <Route path="profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="saved" element={<ProtectedRoute><SavedNews /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="settings/telegram" element={<ProtectedRoute><TelegramGuide /></ProtectedRoute>} />

        {/* Management Routes - Admin + Leader */}
        <Route path="admin" element={<ProtectedRoute><Navigate to="/admin/market-maps" replace /></ProtectedRoute>} />
        <Route path="admin/market-maps" element={<ProtectedRoute><RoleRoute allowedRoles={['admin', 'leader']}><MarketMapManagement /></RoleRoute></ProtectedRoute>} />
        <Route path="admin/market-maps/create" element={<ProtectedRoute><RoleRoute allowedRoles={['admin', 'leader']}><MarketMapEditor /></RoleRoute></ProtectedRoute>} />
        <Route path="admin/market-maps/:mapId" element={<ProtectedRoute><RoleRoute allowedRoles={['admin', 'leader']}><MarketMapEditor /></RoleRoute></ProtectedRoute>} />
        <Route path="admin/iframe-domains" element={<ProtectedRoute><RoleRoute allowedRoles={['admin', 'leader']}><IframeDomainManagement /></RoleRoute></ProtectedRoute>} />

        {/* Admin Only Routes */}
        <Route path="admin/users" element={<ProtectedRoute><RoleRoute allowedRoles={['admin']}><UserManagement /></RoleRoute></ProtectedRoute>} />
        <Route path="admin/teams" element={<ProtectedRoute><RoleRoute allowedRoles={['admin']}><TeamManagement /></RoleRoute></ProtectedRoute>} />
        <Route path="admin/push" element={<ProtectedRoute><RoleRoute allowedRoles={['admin']}><PushNotificationAdmin /></RoleRoute></ProtectedRoute>} />
        <Route path="admin/drive" element={<ProtectedRoute><RoleRoute allowedRoles={['admin']}><DriveSettings /></RoleRoute></ProtectedRoute>} />
        <Route path="admin/project-mindmaps" element={<ProtectedRoute><RoleRoute allowedRoles={['admin', 'leader']}><ProjectMindmapAdmin /></RoleRoute></ProtectedRoute>} />
        <Route path="admin/permissions" element={<ProtectedRoute><RoleRoute allowedRoles={['admin']}><PermissionSettings /></RoleRoute></ProtectedRoute>} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
